import { createContext, useContext, useEffect, useState } from "react";
import type { AuthUser } from "./api";
import { authService } from "./api/auth";
import { setSentryUser } from "./observability/sentry";
import {
  getNavigationForRole,
  hasPermission,
  permissions,
  type NavItem,
} from "./permissions";
import type { AdminRole } from "./schema";

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  hasPermission: (
    category: keyof typeof permissions,
    action: string,
  ) => boolean;
  navigationItems: NavItem[];
  logout: () => void;
  refreshUser: () => Promise<void>;
  setRoles?: (role: AdminRole) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const userData = await authService.getMe();
      setUser(userData);
      setSentryUser(
        userData
          ? { id: userData.id, role: userData.roles?.[0] }
          : null,
      );
    } catch (error) {
      setUser(null);
      setSentryUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const logout = () => {
    setUser(null);
    setSentryUser(null);
    authService.logout();
  };

  const checkPermission = (
    category: keyof typeof permissions,
    action: string,
  ): boolean => {
    if (!user?.roles?.length) return false;
    return hasPermission(user.roles, category, action);
  };

  const navigationItems = user ? getNavigationForRole(user.roles) : [];

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        hasPermission: checkPermission,
        navigationItems,
        logout,
        refreshUser: loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function usePermission(
  category: keyof typeof permissions,
  action: string,
): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(category, action);
}

export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  category: keyof typeof permissions,
  action: string,
) {
  return function PermissionProtectedComponent(props: P) {
    const allowed = usePermission(category, action);
    if (!allowed) return null;
    return <Component {...props} />;
  };
}
